import { useQuery } from '@tanstack/react-query';
import type {
  FulfillmentStatus,
  FulfillmentType,
  OrderStatus,
  OrderSummary,
  PaymentMethod,
  PaymentStatus,
  PickupStatus,
} from '@/lib/api-admin/types';
import type { ApiMeta } from '@/lib/http-core';
import { apiClient } from '@/lib/apiClient';
import type { OrderSort } from '@/stores/useOrderFiltersStore';
import type { OrderMall } from '../lib/orderMall';
import type { OrderLabelCode } from '../lib/displayLabelFilter';
import { orderKeywordQuery, type OrderSearchType } from '../lib/orderSearchType';
import { singleFulfillmentType } from '../lib/orderFilterParams';

export interface OrdersQueryParams {
  page?: number;
  size?: number;
  sort?: OrderSort;
  searchType?: OrderSearchType;
  q?: string;
  ordererName?: string | null;
  ordererPhone?: string | null;
  malls?: ReadonlyArray<OrderMall>;
  paymentMethods?: ReadonlyArray<PaymentMethod>;
  displayLabelCodes?: ReadonlyArray<OrderLabelCode>;
  orderStatuses?: ReadonlyArray<OrderStatus>;
  paymentStatuses?: ReadonlyArray<PaymentStatus>;
  fulfillmentTypes?: ReadonlyArray<FulfillmentType>;
  fulfillmentStatuses?: ReadonlyArray<FulfillmentStatus>;
  pickupStatuses?: ReadonlyArray<PickupStatus>;
  dateFrom?: string | null;
  dateTo?: string | null;
  amountMin?: number | null;
  amountMax?: number | null;
  /** QA-524 [상품주문번호별] — 주문마다 상품 줄(lines[])을 같이 받는다. */
  includeLines?: boolean;
}

export interface OrdersPage {
  items: ReadonlyArray<OrderSummary>;
  meta: ApiMeta;
}

/**
 * Query object sent to BE. ID64: status/method axes use **plural** array keys
 * (`orderStatuses` …) serialized by openapi-fetch as repeated query params,
 * which BE `in()`-filters. Singular keys remain accepted by BE but FE only
 * emits the plural form to avoid the merge ambiguity.
 */
interface AdminOrderListQuery {
  page?: number;
  size?: number;
  sort?: OrderSort;
  q?: string;
  searchType?: Exclude<OrderSearchType, 'ORDER_NO'>;
  keyword?: string;
  orderNos?: string[];
  ordererName?: string;
  ordererPhone?: string;
  malls?: OrderMall[];
  paymentMethods?: PaymentMethod[];
  displayLabelCodes?: OrderLabelCode[];
  orderStatuses?: OrderStatus[];
  paymentStatuses?: PaymentStatus[];
  fulfillmentType?: FulfillmentType;
  fulfillmentStatuses?: FulfillmentStatus[];
  pickupStatuses?: PickupStatus[];
  dateFrom?: string;
  dateTo?: string;
  amountMin?: number;
  amountMax?: number;
  includeLines?: boolean;
}

const nonEmpty = <T,>(arr: ReadonlyArray<T> | undefined): T[] | undefined =>
  arr && arr.length > 0 ? [...arr] : undefined;

export const cleanQuery = (params: OrdersQueryParams): AdminOrderListQuery => {
  const out: AdminOrderListQuery = {};
  if (params.page) out.page = Math.max(0, params.page - 1);
  if (params.size) out.size = params.size;
  // 정렬 — BE 가 화이트리스트로 받는다(orderedAt·createdAt → 주문일시, paymentAmount·totalAmount → 결제금액,
  // OrderRepositoryV2Impl.toSortSpecifiers). 종전엔 보내지 않아 머리글을 눌러도 순서가 그대로였다.
  if (params.sort) out.sort = params.sort;
  // QA-611 검색어 유형 — 주문번호는 orderNos(완전일치, 최대 10개), 그 외 searchType + keyword(+ BE 반영 전 대비 q).
  Object.assign(out, orderKeywordQuery(params.searchType, params.q));
  if (params.ordererName) out.ordererName = params.ordererName;
  if (params.ordererPhone) out.ordererPhone = params.ordererPhone;
  const malls = nonEmpty(params.malls);
  if (malls) out.malls = malls;
  const fulfillmentType = singleFulfillmentType(params.fulfillmentTypes);
  if (fulfillmentType) out.fulfillmentType = fulfillmentType;
  if (params.dateFrom) out.dateFrom = params.dateFrom;
  if (params.dateTo) out.dateTo = params.dateTo;
  if (params.amountMin != null) out.amountMin = params.amountMin;
  if (params.amountMax != null) out.amountMax = params.amountMax;
  if (params.includeLines) out.includeLines = true;

  const paymentMethods = nonEmpty(params.paymentMethods);
  if (paymentMethods) out.paymentMethods = paymentMethods;
  // 주문상태(노출 상태) — 목록 상태 칸과 같은 기준. 아래 canonical 축과 함께 보내면 BE 가 AND.
  const displayLabelCodes = nonEmpty(params.displayLabelCodes);
  if (displayLabelCodes) out.displayLabelCodes = displayLabelCodes;
  const orderStatuses = nonEmpty(params.orderStatuses);
  if (orderStatuses) out.orderStatuses = orderStatuses;
  const paymentStatuses = nonEmpty(params.paymentStatuses);
  if (paymentStatuses) out.paymentStatuses = paymentStatuses;
  const fulfillmentStatuses = nonEmpty(params.fulfillmentStatuses);
  if (fulfillmentStatuses) out.fulfillmentStatuses = fulfillmentStatuses;
  const pickupStatuses = nonEmpty(params.pickupStatuses);
  if (pickupStatuses) out.pickupStatuses = pickupStatuses;

  return out;
};

export const ordersQueryKey = (params: OrdersQueryParams) =>
  ['admin', 'orders', params] as const;

export const useOrdersQuery = (params: OrdersQueryParams) =>
  useQuery<OrdersPage>({
    queryKey: ordersQueryKey(params),
    // QA-577 — 목록을 띄워 둔 채 다른 탭에 다녀오면 다시 조회(주문은 계속 들어온다).
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const [data, meta] = await apiClient.request('get', '/admin/v1/orders', {
        params: { query: cleanQuery(params) as never },
      });
      const payload = data as { items: OrderSummary[] };
      return { items: payload.items, meta };
    },
  });
